import React, { useState } from 'react';
import { Upload as UploadIcon, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImageUpload = ({ onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: 'general'
  });

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, JPEG, and PNG files are allowed');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select an image first');
      return;
    }

    setUploading(true);

    try {
      const token = localStorage.getItem('admin_token');
      
      // Upload file
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      
      const uploadResponse = await axios.post(`${API}/admin/upload-image`, uploadFormData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = uploadResponse.data.image_url;

      // Add to gallery
      await axios.post(`${API}/gallery`, {
        image_url: imageUrl,
        title: formData.title || selectedFile.name,
        category: formData.category,
        order: 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Image uploaded successfully!');
      
      // Reset form
      setSelectedFile(null);
      setPreview(null);
      setFormData({ title: '', category: 'general' });
      
      if (onUploadSuccess) onUploadSuccess();
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{
      background: 'white',
      padding: '2rem',
      borderRadius: '15px',
      border: '1px solid #f0f0f0',
      marginBottom: '2rem'
    }}>
      <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', color: '#2c3e50' }}>
        <UploadIcon size={20} style={{ display: 'inline', marginRight: '0.5rem' }} />
        Upload Image from Device
      </h3>

      {/* File Input */}
      <div style={{
        border: '2px dashed #d4a574',
        borderRadius: '10px',
        padding: '2rem',
        textAlign: 'center',
        marginBottom: '1.5rem',
        cursor: 'pointer',
        background: preview ? '#fef5e7' : '#fafafa',
        position: 'relative'
      }}
        onClick={() => document.getElementById('file-input').click()}
      >
        <input
          id="file-input"
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        {preview ? (
          <div style={{ position: 'relative' }}>
            <img
              src={preview}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '300px',
                borderRadius: '10px',
                objectFit: 'contain'
              }}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFile(null);
                setPreview(null);
              }}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'rgba(239, 68, 68, 0.9)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <X size={18} />
            </button>
          </div>
        ) : (
          <>
            <ImageIcon size={48} color="#d4a574" style={{ margin: '0 auto 1rem' }} />
            <p style={{ color: '#2c3e50', marginBottom: '0.5rem', fontWeight: '600' }}>
              Click to select image or drag and drop
            </p>
            <p style={{ color: '#5a6c7d', fontSize: '0.875rem' }}>
              JPG, JPEG, PNG (max 5MB)
            </p>
          </>
        )}
      </div>

      {/* Additional Fields */}
      {selectedFile && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>
              Title (Optional)
            </label>
            <input
              type="text"
              placeholder="Enter image title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #f0f0f0',
                borderRadius: '8px'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #f0f0f0',
                borderRadius: '8px',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="general">General</option>
              <option value="hair">Hair</option>
              <option value="nails">Nails</option>
              <option value="facial">Facial</option>
              <option value="spa">Spa</option>
              <option value="salon">Salon Interior</option>
            </select>
          </div>
        </div>
      )}

      {/* Upload Button */}
      {selectedFile && (
        <button
          onClick={handleUpload}
          disabled={uploading}
          className="btn-primary"
          style={{
            width: '100%',
            padding: '0.75rem',
            opacity: uploading ? 0.6 : 1,
            cursor: uploading ? 'not-allowed' : 'pointer'
          }}
        >
          {uploading ? 'Uploading...' : 'Upload & Add to Gallery'}
        </button>
      )}

      <p style={{ fontSize: '0.75rem', color: '#5a6c7d', marginTop: '1rem', textAlign: 'center' }}>
        Images will be uploaded to the server and added to the gallery page
      </p>
    </div>
  );
};

export default ImageUpload;
